/* eslint-disable no-loop-func,implicit-arrow-linebreak,function-paren-newline */
import React from 'react';
import uuid from 'uuid';
import 'react-toastify/dist/ReactToastify.css';
import { IptcParser, IptcData } from 'ts-node-iptc';
import { ExifParserFactory, ExifData, OrientationTypes } from 'ts-exif-parser';
import { checkFile } from './checkFile';
import {
  checkUpload,
  getNeedsTranscode,
  getUploadUrl,
  pushAsset,
  setUploadAcl,
  transcodeAsset,
} from '../api/vault/assetUpload';
import { saveAsset, updateAsset, deleteAssets } from '../api/vault/assets';
import { editAssetMetadata } from '../components/AssetDetails/api/editAssetMetadata';
import { IAssetCreate } from '../api/vault/assets/interfaces';
import {
  getAssetFromApi,
  getThumbnailRendition,
  getVideoSourceFromPreview,
  putThumbnailRendition,
} from '../components/AssetDetails/api/assetsLookupsMetatags';
import { IconType, TV } from '../constants';
import { uploadMultipartFile } from './multipart';
import { createAssetStatus, getAssetStatus, updateAssetStatus } from '../api/vault/assetStatus/api';
import {
  ActionStatus, ActionType as AssetActionType, IAssetStatus,
} from '../api/vault/assetStatus/interfaces';
import toast, { superAdminToast } from './toast';
import { Product } from '../state/product';
import { VaultUploadStatus } from '../state/vaultUploadDialog';
import { searchAssets } from '../api/vault/searchEngine';
import { sendEmail } from '../api/vault/email';
import { League, Sport } from '../enums';
import { ISystemSetting } from '../api/vault/systemSettings';
import { ILookupOption } from '../state/lookups';
import { setCachedLastScheduleUpload } from '../state/exchange/gameScheduleForm';

export enum UploadToastType {
  uploading = 'uploading',
  transcode = 'transcode',
  transcodeTimeout = 'transcodeTimeout',
  preProcessing = 'preProcessing',
  complianceError = 'complianceError',
  audioError = 'audioError',
  created = 'created',
  uploadError = 'uploadError',
  gopError = 'gopError',
}

// format, width, height, fps, gop
const ExchangeCompliance = [
  ['720p', 1280, 720, 15, 12000],
  ['720p', 1280, 720, 28, 12000],
  ['720p', 1280, 720, 30, 12000],
  ['1080p', 1920, 1080, 15, 15000],
  ['1080i', 1440, 1080, 15, 12000],
  ['1080i', 1920, 1080, 15, 12000],
  ['480i', 720, 480, 15, 8000],
  ['480i', 720, 480, 30, 8000],
];

const checkExchangeCompliant = async (
  assetDetails: any,
  fileData: any,
  systemSettings: ISystemSetting,
  callback: ICallback,
  isVaultSuperAdmin: boolean,
  orgId: number,
  title: string,
): Promise<boolean> => {
  const { league, exchangeSport, exchangeQualifier } = assetDetails;
  const {
    width, height, fixedGop, gop, bitrate, hasAudio, mediaType,
  } = fileData;

  let ignorePost: boolean = false;
  if (assetDetails.exchangeSport === Sport.Softball) ignorePost = true;

  if (!systemSettings) throw Error('Missing System Settings.');

  // Allow gop for specific leagues and sports
  if (!fixedGop || gop > 30) {
    console.log(`Details: ${assetDetails.league}, ${assetDetails.exchangeSport}`);
    if (assetDetails.league && assetDetails.exchangeSport && systemSettings.SkipExchangeGopCheck.includes(`${assetDetails.exchangeSport}:${assetDetails.league}`) && gop >= 0) {
      console.log('Ignoring gop check.');
      ignorePost = true;
    } else {
      let GopError = () => (
        <div>
          <p>GOP incorrect, please verify content →  <a href={`mailto:${systemSettings.ExchangeSupportEmail}`}>Contact Support</a></p>
        </div>
      );
      if (!gop || gop === 0) {
        GopError = () => (
          <div>
            <p>Invalid MP4 File, please verify content →  <a href={`mailto:${systemSettings.ExchangeSupportEmail}`}>Contact Support</a></p>
          </div>
        );
      }
      if (callback.toast) callback.toast(UploadToastType.gopError);
      else superAdminToast(false, GopError, { type: toast.TYPE.WARNING });
      throw Error('Gop incorrect.');
    }
  }

  // VA-1961: interop compliance
  if (exchangeSport === Sport.Football && league === League.NCAA) {
    if (!exchangeCompliant(width, height, gop, bitrate)) {
      if (!systemSettings) {
        throw Error('Missing System Settings.');
      }

      const complianceError = (
        <div>
          <p>Not compliant →  <a href={`mailto:${systemSettings.ExchangeSupportEmail}`}>Contact Support</a></p>
        </div>
      );
      if (callback.toast) callback.toast(UploadToastType.complianceError);
      superAdminToast(false, complianceError, { type: toast.TYPE.WARNING });
      await sendEmail({
        orgId,
        toAddresses: [],
        fromAddress: 'admin@catapultsports.info',
        body: `
          ${title} is not Interop compliant for Exchange upload. Please check these asset properties: <br/>
          <br/>
          Width: ${width} <br/>
          Height: ${height} <br/>
          GOP: ${gop} <br/>
          Media Type: ${mediaType} <br/>
          BitRate: ${bitrate} <br/>
          <br/>
          Contact Support: <a href={mailto:${systemSettings.ExchangeSupportEmail}}>${systemSettings.ExchangeSupportEmail}</a>
        `,
        subject: `Catapult Sports - ${title} is not Interop compliant.`,
        isHtmlBody: true,
      });
      throw Error(`Not compliant - width: ${width}, height: ${height}, gop: ${gop}, bitrate: ${bitrate}`);
    }
    if (exchangeQualifier.includes(TV) && !hasAudio) {
      const audioError = (
        <div>
          <p>Audio not available, please attach audio →  <a href={`mailto:${systemSettings.ExchangeSupportEmail}`}>Contact Support</a></p>
        </div>
      );
      if (callback.toast) callback.toast(UploadToastType.audioError);
      superAdminToast(false, audioError, { type: toast.TYPE.WARNING });
      throw Error('Audio not available.');
    }
  }

  return ignorePost;
};

const exchangeCompliant = (width: number, height: number, gop: number, bitrate: number): boolean => !!ExchangeCompliance.find(([format, w, h, g, b]) => w === width && h === height && g === gop && bitrate <= (b as unknown as number));

const getAssetType = (file: string) => {
  const lower = file.toLowerCase();
  if (lower.endsWith('.mov') || lower.endsWith('.mp4') || lower.endsWith('.m4v') || lower.endsWith('mxf')) {
    return IconType.VIDEO;
  }
  if (lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.tiff') || lower.endsWith('.bmp') || lower.endsWith('.jpeg')) {
    return IconType.IMAGE;
  }
  if (lower.endsWith('.mp3') || lower.endsWith('.wav')) {
    return IconType.AUDIO;
  }
  return IconType.DOCUMENT;
};

const uploadIfNeeded = async (
  file: File, sha1: string, assetStatusId: string, isExchange: boolean = false,
  isVaultSuperAdmin: boolean = false,
  progressUpdateCallback: (stage: string, progressPercentage: string) => void = null,
  callback: ICallback = {},
): Promise<string> => {
  const { data: { data: exists } } = await checkUpload(sha1.toString());
  let ext = file.name.substr(file.name.lastIndexOf('.')).toLowerCase();
  if (isExchange && ext === 'mov') {
    // VA-2296 - if exchange upload, normalize to mp4 on ingest
    ext = 'mp4';
  }

  if (exists === 'false') {
    // get a signed URL to upload to
    const result = await getUploadUrl(sha1 as string, ext);
    // upload the file
    if (callback.toast) callback.toast(UploadToastType.uploading);
    else superAdminToast(false, `Uploading File ${file.name}`, { type: toast.TYPE.INFO });
    const uploadCallback = (progressPercentage: string) => progressUpdateCallback('Uploading', progressPercentage);
    const stat = await uploadMultipartFile(file, result.data.data.upload, assetStatusId, isVaultSuperAdmin, uploadCallback);

    if (stat) {
      // set ACL
      await setUploadAcl(result.data.data.cloudFront);
      const saveurl = result.data.data.cloudFront;
      const aid = uuid.v4();
      await pushAsset(sha1 as string, aid.toString(), saveurl);
      return aid;
    }
  } else {
    const aid = uuid.v4();
    await pushAsset(sha1 as string, aid.toString(), '');
    return aid;
  }

  return null;
};

/**
 * Determine whether or not to transcode newly uploaded asset
 * @param file
 * @param assetId
 * @param asset
 * @param needExchangeTranscode
 * @param isVaultUpload
 * @param isVaultSuperAdmin
 * @param callback.toast
 * @param ignoreGop
 * @returns True if transcode has started, false if transcode is not needed
 */
async function processVideo(
  file: File, assetId: string, asset: any,
  needExchangeTranscode: boolean, isVaultUpload: boolean,
  isVaultSuperAdmin: boolean, callback: ICallback = null,
  ignoreGop: boolean = false,
): Promise<boolean> {
  // do a quick check for a preview rendition...
  let preview = null;
  try {
    preview = await getVideoSourceFromPreview(assetId);
  } catch { /* continue */ }

  let needVaultTranscode = false;
  if (isVaultUpload) {
    try {
      const needTranscodeResponse = await getNeedsTranscode(assetId, isVaultUpload);
      if (needTranscodeResponse && needTranscodeResponse.data && needTranscodeResponse.data.data) {
        needVaultTranscode = needTranscodeResponse.data.data.needsTranscode;
      }
    } catch {
      // If error with the request, set that we need a transcode, as the transcode request should
      // do another check to determine the transcode type required
      needVaultTranscode = true;
      // continue
    }
  }

  if (!preview || needExchangeTranscode || needVaultTranscode) {
    const status = await transcodeAsset(assetId, needExchangeTranscode, true, needVaultTranscode); // Start transcode in backend
    console.log('[QA] Asset Transcode Status: ', status.data);

    return true;
  }
  // try to get the thumbnail from GAMS
  let thumb = null;
  try {
    thumb = await getThumbnailRendition(assetId);
  } catch { /* continue */ }

  if (!thumb) {
    // build one
    try {
      const trythumb = await putThumbnailRendition(assetId, '00:00:02.000');
      if (trythumb) thumb = trythumb.data.data.path;
    } catch { /* continue */ }
  } else {
    // update the asset with the thumbnail URL...
    await editAssetMetadata({
      ...asset,
      thumbnailImageUrl: thumb,
      previewImageUrl: thumb,
    });
  }

  return false;
}

const locationName = (iptcData: IptcData) => {
  if (iptcData.content_location_name) {
    return iptcData.content_location_name;
  }
  if (iptcData.country_or_primary_location_name) {
    if (iptcData.sub_location) {
      return `${iptcData.sub_location}, ${iptcData.country_or_primary_location_name}`;
    }
    return iptcData.country_or_primary_location_name;
  }
  return iptcData.sub_location;
};

const fixStringArray = (data: string[]): string => {
  let retval: string = '';
  data.forEach((str) => {
    if (retval !== '') {
      retval += ', ';
    }
    retval += str;
  });
  return retval;
};

const pushIptcData = (exifData: ExifData, iptcData: IptcData, description: string, categoryLookups: ILookupOption[]): any => {
  let catId = null;
  if (iptcData.category && categoryLookups) {
    catId = categoryLookups.find((key) => key.label === iptcData.category);
  }
  let photoRotation = null;
  if (exifData && exifData.tags && exifData.tags.Orientation) {
    if (exifData.tags.Orientation === OrientationTypes.BOTTOM_RIGHT) {
      photoRotation = 180;
    } else if (exifData.tags.Orientation === OrientationTypes.RIGHT_TOP) {
      photoRotation = 90;
    } else if (exifData.tags.Orientation === OrientationTypes.LEFT_BOTTOM) {
      photoRotation = 270;
    }
  }
  const updateParams = {
    photoRotation,
    headline: iptcData.headline,
    description: description ? `${description}: ${iptcData.caption}` : iptcData.caption,
    instructions: iptcData.special_instructions,
    photoIptcCategory: catId,
    city: iptcData.city,
    country: iptcData.country_or_primary_location_name,
    photographer: iptcData.credit,
    photoTimestamp: iptcData.date_time ? iptcData.date_time.toLocaleString() : null,
    tags: iptcData.keywords ? fixStringArray(iptcData.keywords) : null,
    gameDate: iptcData.date_time,
    event: iptcData.subject_reference,
    location: locationName(iptcData),
  };
  return updateParams;
};

const getImageData = (file: File, description: string, categoryLookups: ILookupOption[]): Promise<any> => new Promise(async (resolve) => {
  let exifData: ExifData = null;
  let iptcData: IptcData = null;
  const fileReader = new FileReader();
  fileReader.onload = async (data) => {
    try {
      const ArrayBuffer = data.target.result as ArrayBuffer;
      exifData = ExifParserFactory.create(ArrayBuffer).parse();
      iptcData = IptcParser.parse(Buffer.from(ArrayBuffer));
      resolve(pushIptcData(exifData, iptcData, description, categoryLookups));
    } catch (e) {
      console.log('Error getting photo metadata, ignoring');
      resolve(null);
    }
  };
  fileReader.readAsArrayBuffer(file);
});

interface ICallback {
  status?: (uploadStatus: VaultUploadStatus) => void;
  progress?: (progressPercentage: string) => void;
  toast?: (toastType: UploadToastType) => void;
  assetId?: (assetId: string) => void;
}

export const upload = (
  file: File, orgId: number, videoType: string,
  systemSettings: ISystemSetting, assetDetails: any = {},
  callback: ICallback = {},
  isVaultSuperAdmin: boolean = false, iptcCategoryOptions: ILookupOption[] = null,
) => new Promise(async (resolve) => {
  if (callback.toast) callback.toast(UploadToastType.preProcessing);
  else superAdminToast(false, `Pre-Processing file ${file.name}`, { type: toast.TYPE.INFO });

  if (callback.status) callback.status(VaultUploadStatus.Initializing);

  const isVaultUpload = videoType === Product.Vault;
  const ignoreVideoCalculations = (videoType === Product.Vault || videoType === Product.MyVault);
  const isExchange = videoType === Product.Exchange;
  const title = file.name.substr(0, file.name.lastIndexOf('.'));
  const assetType = getAssetType(file.name);
  let imageData: any = null;
  const description: string = assetDetails && assetDetails.description && assetDetails.description !== '' ? assetDetails.description : 'Asset Imported to Vault';

  if (assetType === IconType.IMAGE) {
    imageData = await getImageData(file, description, iptcCategoryOptions);
  }

  // Create asset here
  const params: IAssetCreate = {
    title,
    description,
    type: assetType,
    videoType,
    fileName: file.name,
  };

  // Check for exchange reupload
  let reuploadAssetId: string = null;
  if (isExchange && assetDetails) {
    params.gameId = assetDetails.gameId;
    params.exchangeQualifier = assetDetails.exchangeQualifier;
    params.exchangeScheduleId = assetDetails.exchangeScheduleId;
    if (assetDetails.exchangeVideoAngle) params.exchangeVideoAngle = assetDetails.exchangeVideoAngle;
    params.sourceTeamId = assetDetails.sourceTeamId;
    params.newExchangeSchedule = assetDetails.newExchangeSchedule;
    if (assetDetails.gameId) { // Check for new uploads if not missing gameId
      const existingAssets = await searchAssets({
        queryString: '',
        size: 10,
        page: 1,
        filters: [
          ['gameId', [assetDetails.gameId]], // TODO: This should be changed to scheduleId
          ['exchangeQualifier', [assetDetails.exchangeQualifier]],
          ['sourceTeamId', [assetDetails.sourceTeamId]],
          ...(assetDetails.exchangeVideoAngle ? [['exchangeVideoAngle', [assetDetails.exchangeVideoAngle]]] : []),
        ],
      });
      // Reupload
      if (existingAssets.data.data.hits.total.value !== 0) {
        reuploadAssetId = existingAssets.data.data.hits.hits[0]._id;
      }
    }
  }

  let assetId: string;
  try {
    if (reuploadAssetId) {
      assetId = reuploadAssetId;
    } else {
      const newAsset = (await saveAsset(params)).data.data;
      assetId = newAsset._id;
      setCachedLastScheduleUpload(newAsset.exchangeScheduleId);
      if (callback.assetId) callback.assetId(assetId);
    }
  } catch (e) {
    let errorMessage = `Error uploading: ${file.name}`;
    const error = e as any;
    if (error.response && error.response.data && error.response.data.message) {
      errorMessage = `Error uploading: ${error.response.data.message}`;
    }
    if (callback.toast) callback.toast(UploadToastType.uploadError);
    else superAdminToast(false, errorMessage, { type: toast.TYPE.ERROR });
    if (callback.status) callback.status(VaultUploadStatus.Error);
    resolve(false);
    return;
  }

  // Create asset status
  const assetStatus: IAssetStatus = (await createAssetStatus({
    assetName: title,
    assetId,
    actionType: AssetActionType.Upload,
    actionStatus: ActionStatus.InProgress,
    actionProgress: 'Pre-Processing - 0',
    videoType,
    orgId,
  })).data.data;
  const assetStatusId: string = assetStatus._id;

  const updateProgress = async (stage: string, progressPercentage: string) => {
    if (callback.progress) callback.progress(progressPercentage);
    await updateAssetStatus(assetStatusId, { actionProgress: `${stage} - ${progressPercentage}` });
  };

  if (window.EXCHANGE_UPLOAD_CANCELED) {
    await deleteAssets({ ids: [assetId] });
    return;
  }
  if (callback.status) callback.status(VaultUploadStatus.PreProcessing);

  checkFile(file, assetType === IconType.VIDEO, ignoreVideoCalculations, updateProgress).then(async (fileData) => {
    const {
      sha1, width, height, fps, gop, fixedGop, bitrate, hasAudio, mediaType,
    } = fileData;

    await updateAssetStatus(assetStatusId, { actionProgress: '0' });
    if (callback.status) callback.status(VaultUploadStatus.Uploading);

    try {
      // Check exchange compliance before upload
      let ignorePost: boolean = false;
      if (params.type === IconType.VIDEO && isExchange) {
        ignorePost = await checkExchangeCompliant(assetDetails, fileData, systemSettings, callback, isVaultSuperAdmin, orgId, title);
      }

      if (!isExchange) {
        // eslint-disable-next-line no-param-reassign
        delete assetDetails.description;
        // eslint-disable-next-line no-param-reassign
        delete assetDetails.fileName;
      }

      const aid = await uploadIfNeeded(file, sha1.toString(), assetStatusId, isExchange, isVaultSuperAdmin, updateProgress, callback);
      const updateParams = {
        id: assetId,
        sha1: sha1.toString(),
        oldVaultID: aid,
        ...imageData,
        ...assetDetails,
      };

      // Determine if needing to determine transcode
      if (callback.status) callback.status(VaultUploadStatus.CheckTranscode);

      // Update the asset with sha1 and aid
      await updateAsset(assetId, updateParams);

      // Fetching updated asset - if exchange will provide rebuilt title
      const asset = await getAssetFromApi(assetId);
      const {
        title: assetName, codec, exchangeSport, league,
      } = asset;

      // Create thumbnail for asset
      if (params.type === IconType.VIDEO) {
        const exchangeFormat = (width === 1280 && height === 720 && fps >= 59.92 && fps <= 60.3 && bitrate <= 12288 && ([30, 28, 15].includes(gop)) && fixedGop);
        const dontTranscodeUpload = isExchange && (systemSettings.DontTranscodeUploads || []).includes(`${exchangeSport}:${league}`);
        const needExchangeTranscode = isExchange && !exchangeFormat && !dontTranscodeUpload;

        if (dontTranscodeUpload) {
          console.log(`[QA] "${exchangeSport}:${league}" is listed in DontTranscodeUploads - master rendition for "${file.name}" will not be transcoded`);
        }

        let skipTranscode = false;
        if (codec && codec.toLowerCase() === 'hevc' && systemSettings.AllowH265Uploads.includes(`${exchangeSport}:${league}`)) {
          // Skip tracking transcode, transcode but don't update status to transcoding
          superAdminToast(false, `Attempting transcode for H265 upload: ${file.name}`, { type: toast.TYPE.INFO });
          skipTranscode = true;
          await processVideo(file, assetId, asset, needExchangeTranscode, isVaultUpload, isVaultSuperAdmin, callback, ignorePost);
        } else if (dontTranscodeUpload) {
          // Master stays untranscoded; still attempt a preview rendition but don't track it or fail the upload over it
          superAdminToast(false, `Attempting preview rendition for non-transcoded upload: ${file.name}`, { type: toast.TYPE.INFO });
          skipTranscode = true;
          try {
            await processVideo(file, assetId, asset, needExchangeTranscode, isVaultUpload, isVaultSuperAdmin, callback, ignorePost);
          } catch { /* Preview is best-effort for non-transcoded uploads */ }
        }

        if (!skipTranscode) {
          const transcoding = await processVideo(file, assetId, asset, needExchangeTranscode, isVaultUpload, isVaultSuperAdmin, callback, ignorePost);
          if (transcoding) {
            await updateAssetStatus(
              assetStatusId,
              {
                assetName,
                actionStatus: ActionStatus.Transcoding,
                actionProgress: null,
              },
            );
            if (callback.toast) callback.toast(UploadToastType.created);
            else superAdminToast(false, `Successful Upload of ${file.name}, Starting Background Processing`, { type: toast.TYPE.SUCCESS });

            if (callback.status) callback.status(VaultUploadStatus.Complete); // Client upload is complete
            resolve(asset);
            return;
          }
        }
      } else if (params.type === IconType.IMAGE) {
        await putThumbnailRendition(assetId, '00:00:00.000');
      }

      // Check if asset status was deleted
      try {
        await getAssetStatus(assetStatusId);
      } catch (e) {
        await createAssetStatus(assetStatus);
      }

      await updateAssetStatus(
        assetStatusId,
        {
          assetName,
          actionStatus: ActionStatus.Completed,
          actionProgress: '100',
        },
      );

      if (callback.toast) callback.toast(UploadToastType.created);
      else superAdminToast(false, `Successful Upload of ${file.name}`, { type: toast.TYPE.SUCCESS });

      if (callback.status) callback.status(VaultUploadStatus.Complete);

      resolve(asset);
    } catch (e) {
      if (window.EXCHANGE_UPLOAD_CANCELED) return;

      // note: ensure asset status marked as failed
      if (assetStatusId) {
        await updateAssetStatus(assetStatusId, {
          actionStatus: ActionStatus.Failed,
          actionProgress: '',
          errorExchangeScheduleId: isExchange ? params.exchangeScheduleId : null,
          errorAssetDetails: isExchange ? params : null,
        });
      }

      if (callback.toast) callback.toast(UploadToastType.uploadError);
      else superAdminToast(false, `Error uploading: ${file.name}`, { type: toast.TYPE.ERROR });
      if (callback.status) callback.status(VaultUploadStatus.Error);

      console.log('Hit error upload', e.toString());

      // note: ensure if asset created, it's cleaned up
      if (assetId) await deleteAssets({ ids: [assetId] });
      resolve(false);
    }
  }, (err) => { /** Ignore, canceled */ });
});
